import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonContent,
} from "@ionic/react";


// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion

// Question Type Definitions
interface BaseQuestion {
    question: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    answer: string;
}




const LivingNonLiving: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const modal = useRef<HTMLIonModalElement>(null);

    const loadRandomSyllabusQuestions = async () => {

        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "ClassThreeScience/LivingNonLiving.json"
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/ClassThreeScience/LivingNonLiving.json`
                );
                data = module.default as Question[];
            }

            setQuestions(data);
            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
            setCurrentQuestion(shuffledQuestions[currentIndex]);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.answer;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false") {
            correctAnswer = currentQuestion.answer;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                    setCurrentQuestion(questions[nextIndex]);
                }
                else {
                    setCurrentIndex(0);
                    loadRandomSyllabusQuestions();
                }
            }, 5000);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "Living and Non-Living Things – Class 3 Science | Mama’s Guidance";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Explore Living and Non-Living Things with fun examples and clear explanations for Class 3 Science students. Learn key differences and characteristics with interactive content at Mama’s Guidance." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>🌱 Chapter 1: Living and Non-living Things</h1>

                            <section>
                                <h2><span className="emoji">🧬</span>What Are Living Things?</h2>
                                <p>Living things are things that are <strong>alive</strong>. They can grow, move, breathe, eat, feel, and reproduce.</p>
                                <h3>Examples of Living Things:</h3>
                                <ul>
                                    <li>Humans</li>
                                    <li>Animals</li>
                                    <li>Plants</li>
                                    <li>Birds</li>
                                    <li>Insects</li>
                                </ul>
                                <h3>Characteristics of Living Things:</h3>
                                <ul>
                                    <li>They <strong>grow</strong></li>
                                    <li>They <strong>move</strong></li>
                                    <li>They <strong>need food</strong></li>
                                    <li>They <strong>breathe</strong></li>
                                    <li>They <strong>feel</strong></li>
                                    <li>They <strong>reproduce</strong></li>
                                    <li>They <strong>die</strong></li>
                                </ul>
                            </section>

                            <section>
                                <h2><span className="emoji">🪨</span>What Are Non-living Things?</h2>
                                <p>Non-living things are things that are <strong>not alive</strong>. They do not grow, move, eat, breathe, or reproduce.</p>
                                <h3>Examples of Non-living Things:</h3>
                                <ul>
                                    <li>Rock</li>
                                    <li>Chair</li>
                                    <li>Pencil</li>
                                    <li>Toys</li>
                                    <li>Car</li>
                                </ul>
                                <p>Some are <strong>natural</strong> like air and water, and some are <strong>man-made</strong> like clothes and computers.</p>
                            </section>

                            <section>
                                <h2><span className="emoji">🆚</span>Living vs Non-living – Comparison Table</h2>
                                <table className="table table-bordered">
                                    <thead>
                                        <tr>
                                            <th>Feature</th>
                                            <th>Living Things</th>
                                            <th>Non-living Things</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Movement</td>
                                            <td>Yes</td>
                                            <td>No</td>
                                        </tr>
                                        <tr>
                                            <td>Growth</td>
                                            <td>Yes</td>
                                            <td>No</td>
                                        </tr>
                                        <tr>
                                            <td>Needs food</td>
                                            <td>Yes</td>
                                            <td>No</td>
                                        </tr>
                                        <tr>
                                            <td>Breathing</td>
                                            <td>Yes</td>
                                            <td>No</td>
                                        </tr>
                                        <tr>
                                            <td>Reproduction</td>
                                            <td>Yes</td>
                                            <td>No</td>
                                        </tr>
                                        <tr>
                                            <td>Made of cells</td>
                                            <td>Yes</td>
                                            <td>No</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </section>

                            <section>
                                <h2><span className="emoji">🧪</span>Fun Fact!</h2>
                                <p>Even though the <strong>sun</strong>, <strong>water</strong>, and <strong>air</strong> are important for life, they are still <strong>non-living</strong> because they don’t grow, move, or reproduce!</p>
                            </section>

                            <section className="summary">
                                <h2><span className="emoji">🧠</span>Summary</h2>
                                <ul>
                                    <li>Living things grow, move, eat, breathe, feel, and reproduce.</li>
                                    <li>Non-living things cannot do any of those things.</li>
                                    <li>Living things can be humans, animals, or plants.</li>
                                    <li>Non-living things can be natural (rocks, water) or man-made (toys, pencils).</li>
                                </ul>
                            </section>

                            <section className="activity">
                                <h2><span className="emoji">📚</span>Fun Activities</h2>
                                <ul>
                                    <li>🔍 Go on a hunt and list 5 living and 5 non-living things you see around you.</li>
                                    <li>🎨 Draw 3 living and 3 non-living things.</li>
                                    <li>📊 Make a mini chart comparing living and non-living things using stickers or photos.</li>
                                </ul>
                            </section>
                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>🌱 Class 3 Science – Chapter 1: Living and Non-living Things</h1>

                        <section>
                            <h2><span className="emoji">🧬</span>What Are Living Things?</h2>
                            <p>Living things are things that are <strong>alive</strong>. They can grow, move, breathe, eat, feel, and reproduce.</p>
                            <h3>Examples of Living Things:</h3>
                            <ul>
                                <li>Humans</li>
                                <li>Animals</li>
                                <li>Plants</li>
                                <li>Birds</li>
                                <li>Insects</li>
                            </ul>
                            <h3>Characteristics of Living Things:</h3>
                            <ul>
                                <li>They <strong>grow</strong></li>
                                <li>They <strong>move</strong></li>
                                <li>They <strong>need food</strong></li>
                                <li>They <strong>breathe</strong></li>
                                <li>They <strong>feel</strong></li>
                                <li>They <strong>reproduce</strong></li>
                                <li>They <strong>die</strong></li>
                            </ul>
                        </section>

                        <section>
                            <h2><span className="emoji">🪨</span>What Are Non-living Things?</h2>
                            <p>Non-living things are things that are <strong>not alive</strong>. They do not grow, move, eat, breathe, or reproduce.</p>
                            <h3>Examples of Non-living Things:</h3>
                            <ul>
                                <li>Rock</li>
                                <li>Chair</li>
                                <li>Pencil</li>
                                <li>Toys</li>
                                <li>Car</li>
                            </ul>
                            <p>Some are <strong>natural</strong> like air and water, and some are <strong>man-made</strong> like clothes and computers.</p>
                        </section>

                        <section>
                            <h2><span className="emoji">🆚</span>Living vs Non-living – Comparison Table</h2>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Feature</th>
                                        <th>Living Things</th>
                                        <th>Non-living Things</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Movement</td>
                                        <td>Yes</td>
                                        <td>No</td>
                                    </tr>
                                    <tr>
                                        <td>Growth</td>
                                        <td>Yes</td>
                                        <td>No</td>
                                    </tr>
                                    <tr>
                                        <td>Needs food</td>
                                        <td>Yes</td>
                                        <td>No</td>
                                    </tr>
                                    <tr>
                                        <td>Breathing</td>
                                        <td>Yes</td>
                                        <td>No</td>
                                    </tr>
                                    <tr>
                                        <td>Reproduction</td>
                                        <td>Yes</td>
                                        <td>No</td>
                                    </tr>
                                    <tr>
                                        <td>Made of cells</td>
                                        <td>Yes</td>
                                        <td>No</td>
                                    </tr>
                                </tbody>
                            </table>
                        </section>

                        <section>
                            <h2><span className="emoji">🧪</span>Fun Fact!</h2>
                            <p>Even though the <strong>sun</strong>, <strong>water</strong>, and <strong>air</strong> are important for life, they are still <strong>non-living</strong> because they don’t grow, move, or reproduce!</p>
                        </section>

                        <section className="summary">
                            <h2><span className="emoji">🧠</span>Summary</h2>
                            <ul>
                                <li>Living things grow, move, eat, breathe, feel, and reproduce.</li>
                                <li>Non-living things cannot do any of those things.</li>
                                <li>Living things can be humans, animals, or plants.</li>
                                <li>Non-living things can be natural (rocks, water) or man-made (toys, pencils).</li>
                            </ul>
                        </section>

                        <section className="activity">
                            <h2><span className="emoji">📚</span>Fun Activities</h2>
                            <ul>
                                <li>🔍 Go on a hunt and list 5 living and 5 non-living things you see around you.</li>
                                <li>🎨 Draw 3 living and 3 non-living things.</li>
                                <li>📊 Make a mini chart comparing living and non-living things using stickers or photos.</li>
                            </ul>
                        </section>
                    </article>
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.answer.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.answer).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default LivingNonLiving;
